import React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import FormControl from '@mui/joy/FormControl';
import FormHelperText from '@mui/joy/FormHelperText';
import CircularProgress from '@mui/joy/CircularProgress';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { getInventorySyncSettings, updateInventorySyncSettings } from '../../inventorySyncApi';
import {
    SETTINGS_FIELDS,
    SettingsForm,
    diffSettings,
    formToSettings,
    settingsToForm,
} from '../../inventorySyncSettings';
import { InventorySyncSettingsResponse, RateLimitSettings } from '../../../../models/inventorySync';
import ConfirmDialog from '../../../../components/confirmDialog/ConfirmDialog';
import { NotificationService } from '../../../../common/notification';
import { HintLabel } from './shared';

function sameForm(a: SettingsForm, b: SettingsForm): boolean {
    return SETTINGS_FIELDS.every(f => a[f.key].trim() === b[f.key].trim());
}

export function SettingsSubTab(props: { isActive: boolean }) {
    const { isActive } = props;
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [data, setData] = React.useState<InventorySyncSettingsResponse | null>(null);
    const [form, setForm] = React.useState<SettingsForm | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [formErrors, setFormErrors] = React.useState<string[]>([]);
    const [pending, setPending] = React.useState<{ settings: RateLimitSettings; changes: string[] } | null>(null);
    const autoLoadedRef = React.useRef(false);

    const loadSettings = React.useCallback(async () => {
        setPending(null);
        setLoading(true);
        setError(null);
        setFormErrors([]);
        try {
            const result = await getInventorySyncSettings();
            if (result.result && result.value) {
                setData(result.value);
                setForm(settingsToForm(result.value.current));
            } else {
                setError('Не удалось загрузить лимиты синка');
            }
        } catch {
            setError('Ошибка запроса лимитов синка');
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (isActive && !autoLoadedRef.current) {
            autoLoadedRef.current = true;
            void loadSettings();
        }
    }, [isActive, loadSettings]);

    const currentForm = React.useMemo(() => data ? settingsToForm(data.current) : null, [data]);
    const defaultsForm = React.useMemo(() => data ? settingsToForm(data.defaults) : null, [data]);
    const dirty = form != null && currentForm != null && !sameForm(form, currentForm);
    const readOnly = !data?.canPersist;

    const requestSave = () => {
        if (!form || !data) {
            return;
        }
        const parsed = formToSettings(form);
        setFormErrors(parsed.errors);
        if (!parsed.settings) {
            return;
        }
        const changes = diffSettings(data.current, parsed.settings);
        if (changes.length === 0) {
            setForm(settingsToForm(data.current));
            return;
        }
        setPending({ settings: parsed.settings, changes });
    };

    const confirmSave = async () => {
        if (!pending) {
            return;
        }
        setSaving(true);
        try {
            const result = await updateInventorySyncSettings(pending.settings);
            if (result.ok) {
                setFormErrors([]);
                setData(prev => prev ? { ...prev, current: result.settings } : prev);
                setForm(settingsToForm(result.settings));
                NotificationService.alertSuccess('Лимиты сохранены. Все процессы применят их в течение 30 секунд.');
            } else {
                setFormErrors(result.errors);
                NotificationService.alertError(result.errors.join('; '), 'Лимиты не сохранены');
            }
        } finally {
            setSaving(false);
            setPending(null);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
                <Button
                    size="sm"
                    variant="outlined"
                    startDecorator={<KeyboardArrowRightIcon />}
                    onClick={loadSettings}
                    disabled={loading || saving}
                    loading={loading}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Обновить
                </Button>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size="sm" />
                    <Typography level="body-sm">Загрузка...</Typography>
                </Box>
            )}

            {!loading && error && (
                <Typography color="danger" level="body-md">{error}</Typography>
            )}

            {!loading && !error && data && form && defaultsForm && (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <Typography level="title-md">Лимиты синка через прокси</Typography>
                        <Typography level="body-xs" color="neutral">
                            Хранятся в Redis и применяются всеми процессами не позже чем через 30 с, без деплоя.
                            Изменение пишется в лог с id администратора.
                        </Typography>
                    </Box>

                    {readOnly && (
                        <Typography color="warning" level="body-sm">
                            Redis не настроен — лимиты нельзя изменить для всех процессов. Показаны значения из appsettings.
                        </Typography>
                    )}

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                        {SETTINGS_FIELDS.map(field => {
                            const changed = currentForm != null && form[field.key].trim() !== currentForm[field.key].trim();
                            return (
                                <FormControl key={field.key} disabled={readOnly || saving}>
                                    <Box sx={{ mb: 0.5 }}>
                                        <HintLabel label={field.label} hint={field.hint} level="body-sm" />
                                    </Box>
                                    <Input
                                        size="sm"
                                        value={form[field.key]}
                                        color={changed ? 'primary' : 'neutral'}
                                        placeholder={field.key === 'profilePageRequestsPerMinute' ? 'общий с JSON API' : undefined}
                                        onChange={e => setForm(prev => prev ? { ...prev, [field.key]: e.target.value } : prev)}
                                    />
                                    <FormHelperText>
                                        {field.bounds} · по умолчанию: {defaultsForm[field.key] || 'общий'}
                                    </FormHelperText>
                                </FormControl>
                            );
                        })}
                    </Box>

                    {formErrors.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {formErrors.map(message => (
                                <Typography key={message} color="danger" level="body-sm">{message}</Typography>
                            ))}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                            size="sm"
                            variant="plain"
                            color="neutral"
                            disabled={readOnly || saving || sameForm(form, defaultsForm)}
                            onClick={() => setForm(defaultsForm)}
                        >
                            Подставить значения по умолчанию
                        </Button>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            disabled={!dirty || saving}
                            onClick={() => {
                                setFormErrors([]);
                                setForm(currentForm);
                            }}
                        >
                            Отменить изменения
                        </Button>
                        <Button
                            size="sm"
                            disabled={readOnly || !dirty || pending != null}
                            loading={saving}
                            onClick={requestSave}
                        >
                            Сохранить
                        </Button>
                    </Box>
                </Sheet>
            )}

            <ConfirmDialog
                title="Изменить лимиты синка?"
                open={pending != null}
                setOpen={open => { if (!open) setPending(null); }}
                onConfirm={confirmSave}
                content={(
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {pending?.changes.map(change => (
                            <Typography key={change} component="li" level="body-sm">{change}</Typography>
                        ))}
                    </Box>
                )}
            >
                Новые лимиты применятся во всех процессах в течение 30 секунд.
            </ConfirmDialog>
        </Box>
    );
}

export default SettingsSubTab;
